import { MenuButton, MenuItem, MenuList } from '@reach/menu-button';
import { device, deviceGte } from '@ui-components/breakpoints';
import { iconAngleDown, iconArrowRight, playGrey15, regularWhite } from '@ui-components/design-tokens';
import { ButtonStyles } from '@ui-components/PlayButton/PlayButton.styles';
import { playFontIcon, playFontSize, regularOutline } from '@ui-components/typography/helpers';
import { SIZE } from '@ui-components/typography/PlayText';
import { PlayTextStyles } from '@ui-components/typography/PlayText/PlayText.styles';
import { playFocus } from '@ui-components/utils.styles';
import styled, { css } from 'styled-components';

export const StyledMenuButton = styled(MenuButton).attrs({
    size: SIZE.SMALL,
    color: regularWhite,
})`
    text-transform: uppercase;
    background: none;
    border: none;
    ${PlayTextStyles}
    text-decoration: none;
    display: block;
    padding: 8px 2px;
    ${playFocus()}
    ${playFontSize(1.3)}
    color: ${regularWhite};

    &:hover,
    &:active,
    &:visited {
        color: ${regularWhite};
    }

    &:after {
        ${playFontIcon(1.7, iconAngleDown)}
        margin-left: 5px;
        margin-bottom: 3px;
        transition: transform 200ms ease-in-out;
    }

    &:hover {
        .no-touchevents && {
            text-decoration: underline;
        }

        &:after {
            text-decoration: none;
        }
    }

    @media ${deviceGte(380)} {
        ${playFontSize(1.5)}
        padding: 10px 8px;
    }

    @media ${device.GteSmall} {
        position: relative;
        ${playFontSize(1.5)}
    }

    @media ${device.GteMedium} {
        padding: 10px 15px;
        ${playFontSize(1.6)}
    }

    &[aria-expanded='true'] {
        background-color: ${playGrey15};

        &:after {
            transform: rotate(-180deg);
        }
    }
`;

export const StyledMenuItem = styled(MenuItem)<{ $isAllItems: boolean }>`
    ${playFontSize(1.4)}
    text-transform: none;
    text-align: left;
    margin: 5px 0;
    letter-spacing: 0;
    white-space: nowrap;
    min-width: 100px;
    padding: 5px 0;

    @media ${device.GteXsmall} {
        padding: 5px 11px;
    }

    @media ${device.GteSmall} {
        ${playFontSize(1.5)}
        min-width: 140px;
    }

    &[data-selected] {
        text-decoration: underline;
        ${regularOutline(0, regularWhite)}

        &:hover {
            outline: none;
        }
    }

    &:hover {
        cursor: pointer;
    }

    ${(props) =>
        props.$isAllItems &&
        css`
            display: inline-block;
        `};
`;

export const StyledSection = styled.div<{ $isAllItems?: boolean; isInstrumental?: boolean }>`
    grid-area: ${(prop) => (prop.isInstrumental ? 'instrumental' : 'category')};

    ${(props) =>
        props.isInstrumental &&
        props.$isAllItems &&
        css`
            column-count: 2;

            @media ${device.GteXsmall} {
                padding: 0 0 0 20px;
                height: 100%;
                column-count: 1;
            }

            @media ${device.GteMedium} {
                padding-left: 0 0 0 40px;
            }
        `}

    ${(props) =>
        !props.isInstrumental &&
        props.$isAllItems &&
        css`
            column-count: 2;
            border-bottom: 1px solid #3b3b3b;
            margin-bottom: 20px;
            padding-bottom: 20px;

            @media ${device.GteXsmall} {
                border-right: 1px solid #3b3b3b;
                border-bottom: 0;
                margin-bottom: 0;
            }

            @media ${device.GteMedium} {
                column-count: 3;
            }
        `}
`;

export const StyledMenuList = styled(MenuList)<{ $isAllItems: boolean }>`
    position: absolute;
    top: 100%;
    left: -0px;
    background-color: ${playGrey15};
    width: 100%;
    border: none;
    outline: none;
    margin-top: -2px;
    z-index: 100;

    @media ${device.GteXsmall} {
        min-width: 350px;
    }

    ${(props) =>
        !props.$isAllItems &&
        css`
            display: grid;
            grid-template-rows: auto;
            grid-template-columns: 1fr 1fr;
            grid-gap: 15px;
            grid-template-areas:
                'category instrumental'
                'cbutton cbutton';

            padding: 5px 15px 20px;
            width: calc(100% - 30px);

            @media ${device.GteXsmall} {
                padding: 5px 5px 20px;
                width: auto;
            }
        `}

    ${(props) =>
        props.$isAllItems &&
        css`
            display: flex;
            flex-direction: column;
            text-align: left;
            padding: 20px;
            width: calc(100% - 40px);

            @media ${device.GteXsmall} {
                grid-template-columns: 2fr 1fr;
                width: calc(100% - 40px);
                flex-direction: row;
            }

            @media ${device.GteSmall} {
                padding: 15px;
                left: -149px;
                width: auto;
            }

            @media ${device.GteMedium} {
                left: -177px;
            }
            @media ${device.GteLarge} {
                left: 0;
            }
        `}
`;

export const StyledButtonContainer = styled.div`
    grid-area: cbutton;
    margin: 0 20px;
`;

export const StyledButtonMenuItem = styled(MenuItem)`
    ${ButtonStyles}
    display: flex;
    justify-content: center;
    align-items: center;

    &[data-selected] {
        background-color: rgba(255, 255, 255, 0.15);
        ${regularOutline(0, regularWhite)}

        &:hover {
            outline: none;
        }
    }

    &:after {
        ${playFontIcon(1.6, iconArrowRight)}
        margin-left: 5px;
    }
`;
